import api from './api';

const radiologyResultService = {
  /**
   * Fetch summary KPI metrics for Radiology Workbench
   */
  async getSummary(centreId = null) {
    const params = centreId ? { centreId } : {};
    const res = await api.get('/radiology/summary', { params });
    return res.data;
  },

  /**
   * Search worklist with multi-criteria filters
   */
  async getWorklist(filter = {}) {
    const res = await api.get('/radiology/worklist', { params: filter });
    return res.data;
  },

  /**
   * Load radiology report detail, patient info, and available templates
   */
  async getReportDetail(testId) {
    const res = await api.get(`/radiology/${testId}`);
    return res.data;
  },

  /**
   * Fetch investigation templates
   */
  async getTemplates(investigationId) {
    const res = await api.get(`/radiology/templates/${investigationId}`);
    return res.data;
  },

  async getTemplatesByInvestigation(investigationId) {
    const res = await api.get(`/radiology/templates/${investigationId}`);
    return res.data;
  },


  /**
   * Save draft narrative report
   */
  async saveDraft(testId, payload) {
    const res = await api.post(`/radiology/${testId}/save-draft`, payload);
    return res.data;
  },

  /**
   * Finalize and sign off radiology report
   */
  async finalizeReport(testId, payload) {
    const res = await api.post(`/radiology/${testId}/finalize`, payload);
    return res.data;
  },

  /**
   * Formal revision of finalized report with mandatory clinical reason
   */
  async reviseReport(testId, payload) {
    const res = await api.post(`/radiology/${testId}/revise`, payload);
    return res.data;
  },

  /**
   * Fetch status & revision history
   */
  async getAuditTrail(testId) {
    const res = await api.get(`/radiology/${testId}/audit-trail`);
    return res.data;
  },

  /**
   * Search templates with multi-criteria filters & pagination
   */
  async searchTemplates(params = {}) {
    const res = await api.get('/radiology/templates/search', { params });
    return res.data;
  },

  /**
   * Get template detail by ID
   */
  async getTemplateDetail(templateId) {
    const res = await api.get(`/radiology/templates/detail/${templateId}`);
    return res.data;
  },

  /**
   * Create new radiology template
   */
  async createTemplate(payload) {
    const data = {
      ...payload,
      title: payload.title || payload.templateTitle || '',
      templateTitle: payload.templateTitle || payload.title || ''
    };
    const res = await api.post('/radiology/templates', data);
    return res.data;
  },

  /**
   * Update existing radiology template
   */
  async updateTemplate(templateId, payload) {
    const data = {
      ...payload,
      title: payload.title || payload.templateTitle || '',
      templateTitle: payload.templateTitle || payload.title || ''
    };
    const res = await api.put(`/radiology/templates/${templateId}`, data);
    return res.data;
  },

  /**
   * Deactivate/delete template
   */
  async deleteTemplate(templateId) {
    const res = await api.delete(`/radiology/templates/${templateId}`);
    return res.data;
  },

  /**
   * Duplicate template
   */
  async duplicateTemplate(templateId) {
    const res = await api.post(`/radiology/templates/${templateId}/duplicate`);
    return res.data;
  },

  /**
   * Download DOCX report for patient test
   */
  async exportReportDocx(testId) {
    const res = await api.get(`/radiology/${testId}/export-docx`, {
      responseType: 'blob'
    });
    return res.data;
  },

  /**
   * Download DOCX for template master
   */
  async exportTemplateDocx(templateId) {
    const res = await api.get(`/radiology/templates/${templateId}/export-docx`, {
      responseType: 'blob'
    });
    return res.data;
  },

  /**
   * Download ZIP of multiple templates
   */
  async bulkExportTemplatesZip(investigationId = null, doctorId = null) {
    const params = {};
    if (investigationId) params.investigationId = investigationId;
    if (doctorId) params.doctorId = doctorId;
    const res = await api.get('/radiology/templates/bulk-export-zip', {
      params,
      responseType: 'blob'
    });
    return res.data;
  },

  /**
   * Extract HTML and text from uploaded DOCX file
   */
  async extractDocx(file) {
    const formData = new FormData();
    formData.append('file', file);
    // Content-Type is set automatically by the axios interceptor (FormData detection)
    const res = await api.post('/radiology/templates/extract-docx', formData);
    return res.data;
  },

  /**
   * Bulk import templates from ZIP file
   */
  async bulkImportZip(file, investigationId, doctorId = null, doctorName = null, centreId = null) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('investigationId', investigationId);
    if (doctorId) formData.append('doctorId', doctorId);
    if (doctorName) formData.append('doctorName', doctorName);
    if (centreId) formData.append('centreId', centreId);

    // Content-Type is set automatically by the axios interceptor (FormData detection)
    const res = await api.post('/radiology/templates/bulk-import-zip', formData);
    return res.data;
  },

  /**
   * Bulk export templates as ZIP
   */
  async bulkExportZip(investigationId = null, doctorId = null, centreId = null) {
    const params = {};
    if (investigationId) params.investigationId = investigationId;
    if (doctorId) params.doctorId = doctorId;
    if (centreId) params.centreId = centreId;

    const res = await api.get('/radiology/templates/bulk-export-zip', {
      params,
      responseType: 'blob'
    });
    return res.data;
  },

  /**
   * Fetch prior radiology reports for the patient of the given test
   */
  async getPreviousReports(testId) {
    const res = await api.get(`/radiology/${testId}/previous-reports`);
    return res.data;
  },

  /**
   * Save technician study acquisition details, measurements, protocol, and notes
   */
  async saveTechnicianStudy(testId, payload) {
    const res = await api.post(`/radiology/${testId}/technician-study`, payload);
    return res.data;
  },

  /**
   * Formal amendment of finalized report with required reason
   */
  async amendReport(testId, payload) {
    const res = await api.post(`/radiology/${testId}/amend`, payload);
    return res.data;
  },

  /**
   * Clinician / Staff acknowledgment of critical finding
   */
  async acknowledgeCritical(testId, payload) {
    const res = await api.post(`/radiology/${testId}/acknowledge-critical`, payload);
    return res.data;
  },

  /**
   * Fetch complete department and test catalog with template counts
   */
  async getDepartmentCatalog() {
    const res = await api.get('/radiology/department-catalog');
    return res.data;
  },

  /**
   * Set a specific template as default for an investigation
   */
  async setDefaultTemplate(templateId, investigationId) {
    const res = await api.put(`/radiology/templates/${templateId}/set-default?investigationId=${investigationId}`);
    return res.data;
  },

  /**
   * Bulk import multiple DOCX files for an investigation.
   * IMPORTANT: Do NOT manually set Content-Type — axios sets multipart/form-data with correct boundary.
   */
  async bulkImportDocxFiles(files, investigationId, doctorId = null, doctorName = null, centreId = null) {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    formData.append('investigationId', investigationId);
    if (doctorId) formData.append('doctorId', doctorId);
    if (doctorName) formData.append('doctorName', doctorName);
    if (centreId) formData.append('centreId', centreId);

    // No explicit Content-Type header: axios auto-generates multipart/form-data with boundary
    const res = await api.post('/radiology/templates/bulk-import-docx', formData);
    return res.data;
  }
};

export default radiologyResultService;

